import * as v from "valibot";

export const MAX_STR = 200;
export const MAX_ROWS = 10_000;
export const MAX_BODY_BYTES = 4 * 1024 * 1024;

const IsoDate = v.pipe(v.string(), v.regex(/^\d{4}-\d{2}-\d{2}$/));
const Id = v.pipe(v.string(), v.minLength(1), v.maxLength(MAX_STR));

export const RowSchema = v.object({
  id: Id,
  week: v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(53)),
  dueDate: IsoDate,
  supplier: v.pipe(v.string(), v.maxLength(MAX_STR)),
  amount: v.pipe(v.number(), v.finite()),
  note: v.pipe(v.string(), v.maxLength(MAX_STR)),
  snoozed: v.optional(v.boolean(), false),
});

export const OccurrenceSchema = v.picklist(["monthly", "quarterly", "yearly"]);

const Name = v.pipe(v.string(), v.trim(), v.nonEmpty(), v.maxLength(MAX_STR));

export const CostCenterSchema = v.object({
  id: Id,
  name: Name,
});

export const CompanySchema = v.object({
  id: Id,
  name: Name,
});

export const CompanyNameSchema = v.object({
  name: Name,
});

export const CompaniesSchema = v.pipe(
  v.array(CompanySchema),
  v.maxLength(MAX_ROWS),
);

export const FixedCostSchema = v.object({
  id: Id,
  description: v.pipe(v.string(), v.maxLength(MAX_STR)),
  category: v.pipe(v.string(), v.maxLength(MAX_STR)),
  amount: v.pipe(v.number(), v.finite()),
  dayOfMonth: v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(31)),
  occurrence: OccurrenceSchema,
  startDate: v.nullable(IsoDate),
  endDate: v.nullable(IsoDate),
  costCenterId: v.nullable(Id),
  startMonth: v.optional(
    v.nullable(v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(12))),
    null,
  ),
});

export const StateSchema = v.pipe(
  v.object({
    version: v.pipe(v.number(), v.integer(), v.minValue(0)),
    startBalance: v.pipe(v.number(), v.finite()),
    rows: v.pipe(v.array(RowSchema), v.maxLength(MAX_ROWS)),
    fixedCosts: v.pipe(v.array(FixedCostSchema), v.maxLength(MAX_ROWS)),
    costCenters: v.pipe(v.array(CostCenterSchema), v.maxLength(MAX_ROWS)),
  }),
  v.forward(
    v.partialCheck(
      [["costCenters"]],
      ({ costCenters }) =>
        new Set(costCenters.map((c) => c.id)).size === costCenters.length,
      "duplicate cost center id",
    ),
    ["costCenters"],
  ),
  v.forward(
    v.partialCheck(
      [["costCenters"], ["fixedCosts"]],
      ({ costCenters, fixedCosts }) => {
        const ids = new Set(costCenters.map((c) => c.id));
        return fixedCosts.every(
          (c) => c.costCenterId === null || ids.has(c.costCenterId),
        );
      },
      "unknown cost center",
    ),
    ["fixedCosts"],
  ),
);

export type Row = v.InferOutput<typeof RowSchema>;
export type FixedCost = v.InferOutput<typeof FixedCostSchema>;
export type CostCenter = v.InferOutput<typeof CostCenterSchema>;
export type Company = v.InferOutput<typeof CompanySchema>;
export type Occurrence = v.InferOutput<typeof OccurrenceSchema>;
export type StateInput = v.InferOutput<typeof StateSchema>;

export function isValidRow(r: unknown): r is Row {
  return v.is(RowSchema, r);
}
